import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {testIdProps} from '@github-ui/test-id-props'
import {Button} from '@primer/react'
import {type SyntheticEvent, useCallback} from 'react'

import {FilterBarActionsUI} from '../../api/stats/contracts'
import {useViews} from '../../hooks/use-views'
import {Resources} from '../../strings'
import {SaveViewSplitButton} from '../common/save-view-split-button'
import styles from './filter-input-actions.module.css'

type FilterInputActionsProps = {
  /**
   * Optional handler for the reset changes button. If this callback is not provided,
   * then the button will not be shown.
   */
  onResetChanges?: React.MouseEventHandler<HTMLButtonElement>

  /**
   * Optional handler for the save changes button. If this callback is not provided,
   * then the button will not be shown.
   */
  onSaveChanges?: (event?: SyntheticEvent) => void

  /**
   * Text to display on the save changes button
   */
  saveButtonText?: string

  hideSaveButton?: boolean
  hideSplitSaveButton?: boolean
  hideResetChangesButton?: boolean
}

export function FilterInputActions({
  onResetChanges,
  onSaveChanges,
  saveButtonText,
  hideSaveButton,
  hideSplitSaveButton,
  hideResetChangesButton,
  children,
}: React.PropsWithChildren<FilterInputActionsProps>) {
  const displayButtonEnabled = useFeatureFlag('memex_display_button_config_menu')
  const {currentView, duplicateCurrentViewState} = useViews()

  const handleDuplicateView = useCallback(async () => {
    if (currentView?.number === undefined) return
    await duplicateCurrentViewState(currentView.number, undefined, {
      ui: FilterBarActionsUI,
    })
  }, [currentView, duplicateCurrentViewState])

  if (hideSaveButton && hideResetChangesButton) return null

  const renderSaveButton = () => {
    if (hideSaveButton) return null

    // Unflagged accounts or in Insights, show simple save button
    if (!displayButtonEnabled || hideSplitSaveButton) {
      return (
        <Button
          variant="primary"
          onClick={onSaveChanges}
          disabled={!onSaveChanges}
          {...testIdProps('filter-actions-save-changes-button')}
        >
          {saveButtonText ?? Resources.saveChanges}
        </Button>
      )
    }

    return (
      <SaveViewSplitButton
        onSave={event => onSaveChanges?.(event)}
        onDuplicate={handleDuplicateView}
        saveButtonText={saveButtonText}
        disabled={!onSaveChanges}
        testIdPrefix="filter-actions"
      />
    )
  }

  return (
    <div className={styles.Box}>
      {children}
      <div className={styles.Box_1} {...testIdProps('filter-state-actions')}>
        {!hideResetChangesButton && (
          <Button
            onClick={onResetChanges}
            disabled={!onResetChanges}
            {...testIdProps('filter-actions-reset-changes-button')}
          >
            {Resources.discardChanges}
          </Button>
        )}
        {renderSaveButton()}
      </div>
    </div>
  )
}

try{ FilterInputActions.displayName ||= 'FilterInputActions' } catch {}